import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar, Plus, Edit, Trash2, CheckCircle2, AlertTriangle, Package, Printer, X, ScanBarcode, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { EmptyState } from "../components/shared/EmptyState";
import { StatusBadge } from "../components/shared/StatusBadge";
import { MAINTENANCE_STATUSES } from "../components/shared/constants";
import { SearchFilterBar } from "../components/shared/SearchFilterBar";
import { BarcodeScannerModal } from "../components/shared/BarcodeScannerModal";
import { ExportButton } from "../components/shared/ExportButton";
import { SortableHeader } from "../components/shared/SortableHeader";

const INITIAL_FORM = { machine_id: "", title: "", description: "", scheduled_date: "", status: "scheduled", parts_used: [], recurrence_days: "" };

function PartsSelector({ partsUsed, setPartsUsed, allParts }) {
  const [scannerOpen, setScannerOpen] = useState(false);

  const addPart = () => setPartsUsed([...partsUsed, { part_id: "", quantity: 1 }]);
  const removePart = (i) => setPartsUsed(partsUsed.filter((_, idx) => idx !== i));
  const updatePart = (i, field, val) => {
    const updated = [...partsUsed];
    updated[i] = { ...updated[i], [field]: val };
    setPartsUsed(updated);
  };

  const handleBarcodeScan = async (code) => {
    try {
      const resp = await axios.get(`${API}/parts/barcode-lookup?code=${encodeURIComponent(code)}`, { headers: getAuthHeaders() });
      const foundPart = resp.data;
      // Add part to list or update last empty one
      const emptyIdx = partsUsed.findIndex(p => !p.part_id);
      if (emptyIdx >= 0) {
        updatePart(emptyIdx, "part_id", foundPart.id);
      } else {
        setPartsUsed([...partsUsed, { part_id: foundPart.id, quantity: 1 }]);
      }
      toast.success(`Scanned: ${foundPart.sap_number} — ${foundPart.description}`);
    } catch {
      toast.error(`No part found for code: ${code}`);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[#0F172A] flex items-center gap-1"><Package size={14} /> Parts Used</Label>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => setScannerOpen(true)} className="rounded-none border-[#2563EB] text-[#2563EB] text-xs" data-testid="scan-part-barcode">
            <ScanBarcode size={12} className="mr-1" />Scan
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={addPart} className="rounded-none border-[#E2E8F0] text-xs" data-testid="add-part-line">
            <Plus size={12} className="mr-1" />Add Part
          </Button>
        </div>
      </div>
      {partsUsed.map((pu, i) => (
        <div key={i} className="flex gap-2 items-center" data-testid={`part-line-${i}`}>
          <Select value={pu.part_id} onValueChange={(v) => updatePart(i, "part_id", v)}>
            <SelectTrigger className="flex-1 rounded-none border-[#E2E8F0] text-xs h-8" data-testid={`part-select-${i}`}>
              <SelectValue placeholder="Select part..." />
            </SelectTrigger>
            <SelectContent className="bg-white rounded-none border border-[#E2E8F0]">
              {allParts.map((p) => <SelectItem key={p.id} value={p.id}><span className="font-mono">{p.sap_number}</span> — {p.description}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            type="number"
            min={1}
            value={pu.quantity}
            onChange={(e) => updatePart(i, "quantity", parseInt(e.target.value) || 1)}
            className="w-16 rounded-none border-[#E2E8F0] text-xs h-8 font-mono"
            data-testid={`part-qty-${i}`}
          />
          <Button type="button" size="sm" variant="ghost" onClick={() => removePart(i)} className="text-[#DC2626] h-8 w-8 p-0" data-testid={`remove-part-${i}`}>
            <X size={14} />
          </Button>
        </div>
      ))}
      {partsUsed.length === 0 && <p className="text-xs text-[#94A3B8]">No parts added. Click "Add Part" or "Scan" to add parts.</p>}
      <BarcodeScannerModal open={scannerOpen} onClose={setScannerOpen} onScan={handleBarcodeScan} />
    </div>
  );
}

function PartsOrderPrintView({ orderData }) {
  if (!orderData) return null;
  return (
    <div id="parts-order-print" className="hidden print:block p-8 bg-white text-black" style={{ fontFamily: "Arial, sans-serif" }}>
      <div style={{ borderBottom: "3px solid #000", paddingBottom: "16px", marginBottom: "24px" }}>
        <h1 style={{ fontSize: "22px", fontWeight: "bold", margin: 0 }}>PARTS ORDER REQUEST FORM</h1>
        <p style={{ color: "#666", fontSize: "12px", margin: "4px 0 0 0" }}>Vault — Auto-Generated</p>
      </div>
      <table style={{ width: "100%", marginBottom: "20px", fontSize: "14px" }}>
        <tbody>
          <tr><td style={{ fontWeight: "bold", width: "140px", padding: "4px 0" }}>Work Order:</td><td>{orderData.title}</td></tr>
          <tr><td style={{ fontWeight: "bold", padding: "4px 0" }}>Machine #:</td><td style={{ fontFamily: "monospace" }}>{orderData.machine_number}</td></tr>
          <tr><td style={{ fontWeight: "bold", padding: "4px 0" }}>Date:</td><td>{orderData.date}</td></tr>
          <tr><td style={{ fontWeight: "bold", padding: "4px 0" }}>Generated:</td><td>{new Date().toLocaleString()}</td></tr>
        </tbody>
      </table>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
        <thead>
          <tr style={{ backgroundColor: "#F1F5F9" }}>
            {["SAP #", "Description", "Manufacturer", "Mfg Part #", "Vendor", "Vendor Part #", "Qty"].map((h) => (
              <th key={h} style={{ border: "1px solid #CBD5E1", padding: "8px", textAlign: "left", fontWeight: "bold", fontSize: "11px", textTransform: "uppercase" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {orderData.order_lines.map((line, i) => (
            <tr key={i}>
              <td style={{ border: "1px solid #CBD5E1", padding: "6px 8px", fontFamily: "monospace", fontWeight: "bold" }}>{line.sap_number}</td>
              <td style={{ border: "1px solid #CBD5E1", padding: "6px 8px" }}>{line.description}</td>
              <td style={{ border: "1px solid #CBD5E1", padding: "6px 8px" }}>{line.manufacturer || "-"}</td>
              <td style={{ border: "1px solid #CBD5E1", padding: "6px 8px", fontFamily: "monospace" }}>{line.manufacturer_part_number || "-"}</td>
              <td style={{ border: "1px solid #CBD5E1", padding: "6px 8px" }}>{line.vendor || "-"}</td>
              <td style={{ border: "1px solid #CBD5E1", padding: "6px 8px", fontFamily: "monospace" }}>{line.vendor_part_number || "-"}</td>
              <td style={{ border: "1px solid #CBD5E1", padding: "6px 8px", textAlign: "center", fontWeight: "bold" }}>{line.quantity}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: "40px", borderTop: "1px solid #CBD5E1", paddingTop: "16px", display: "flex", justifyContent: "space-between" }}>
        <div><p style={{ fontSize: "12px", color: "#666" }}>Requested By: ______________________</p></div>
        <div><p style={{ fontSize: "12px", color: "#666" }}>Approved By: ______________________</p></div>
        <div><p style={{ fontSize: "12px", color: "#666" }}>Date: ______________________</p></div>
      </div>
    </div>
  );
}

function MaintenanceCard({ record, onEdit, onDelete, onPrintOrder, canDelete }) {
  const statusIcons = {
    completed: <CheckCircle2 size={16} className="text-[#10B981]" />,
    overdue: <AlertTriangle size={16} className="text-[#DC2626]" />,
  };
  const hasParts = record.parts_used && record.parts_used.length > 0;
  const hasRecurrence = record.recurrence_days && record.recurrence_days > 0;

  return (
    <div className="bg-white border border-[#E2E8F0] p-6 hover:border-[#0F172A] transition-colors duration-150" data-testid={`maintenance-card-${record.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            {statusIcons[record.status] || <Calendar size={16} className="text-[#EAB308]" />}
            <h3 className="font-heading font-bold text-lg text-[#0F172A]">{record.title}</h3>
            <StatusBadge status={record.status} colorMap={MAINTENANCE_STATUSES} />
            {hasParts && <span className="text-xs font-mono bg-[#F0F9FF] text-[#2563EB] px-2 py-0.5 border border-[#BAE6FD]">{record.parts_used.length} part(s)</span>}
            {hasRecurrence && <span className="text-xs font-mono bg-[#F0FDF4] text-[#10B981] px-2 py-0.5 border border-[#BBF7D0]" data-testid={`recurrence-badge-${record.id}`}>Every {record.recurrence_days}d</span>}
          </div>
          <div className="space-y-1 text-sm mb-3">
            {record.machine_number && (
              <div><span className="text-[#64748B]">Machine:</span><span className="ml-2 font-mono text-[#0F172A] font-medium">{record.machine_number}</span></div>
            )}
            <div><span className="text-[#64748B]">Scheduled:</span><span className="ml-2 text-[#0F172A]">{record.scheduled_date}</span></div>
          </div>
          <p className="text-[#64748B] text-sm">{record.description}</p>
        </div>
        <div className="flex gap-2 ml-4">
          {hasParts && (
            <Button size="sm" variant="outline" onClick={() => onPrintOrder(record.id)} className="rounded-none border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white" data-testid={`print-order-${record.id}`}>
              <Printer size={14} />
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onEdit(record)} className="rounded-none border-[#E2E8F0]" data-testid={`edit-maintenance-${record.id}`}><Edit size={14} /></Button>
          {canDelete && <Button size="sm" variant="outline" onClick={() => onDelete(record.id)} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white" data-testid={`delete-maintenance-${record.id}`}><Trash2 size={14} /></Button>}
        </div>
      </div>
    </div>
  );
}

function MaintenanceFormDialog({ open, onOpenChange, editingRecord, formData, setFormData, machines, allParts, onSubmit, onReset }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) onReset(); }}>
      <DialogTrigger asChild>
        <Button className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none transition-colors duration-150 tracking-wide" data-testid="add-maintenance-button">
          <Plus size={16} className="mr-2" />ADD RECORD
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="maintenance-form-description">
        <DialogHeader><DialogTitle className="font-heading font-bold text-[#0F172A]">{editingRecord ? "Edit Maintenance" : "Add Maintenance"}</DialogTitle></DialogHeader>
        <div id="maintenance-form-description" className="sr-only">Form to create or edit maintenance records</div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="text-[#0F172A]">Machine *</Label>
            <Select value={formData.machine_id} onValueChange={(v) => setFormData({ ...formData, machine_id: v })} required>
              <SelectTrigger className="mt-1 rounded-none border-[#E2E8F0]" data-testid="machine-select"><SelectValue placeholder="Select machine" /></SelectTrigger>
              <SelectContent className="bg-white rounded-none border border-[#E2E8F0]">
                {machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.machine_number} - {m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-[#0F172A]">Title *</Label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="mt-1 rounded-none border-[#E2E8F0]" data-testid="maintenance-title-input" /></div>
          <div><Label className="text-[#0F172A]">Description *</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required className="mt-1 rounded-none border-[#E2E8F0]" rows={3} data-testid="maintenance-description-input" /></div>
          <div><Label className="text-[#0F172A]">Scheduled Date *</Label><Input type="date" value={formData.scheduled_date} onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })} required className="mt-1 rounded-none border-[#E2E8F0]" data-testid="maintenance-date-input" /></div>
          <div>
            <Label className="text-[#0F172A]">Status *</Label>
            <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })} required>
              <SelectTrigger className="mt-1 rounded-none border-[#E2E8F0]" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white rounded-none border border-[#E2E8F0]">
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#0F172A]">Recurrence (days)</Label>
            <Input
              type="number"
              min={0}
              value={formData.recurrence_days}
              onChange={(e) => setFormData({ ...formData, recurrence_days: e.target.value ? parseInt(e.target.value) : "" })}
              placeholder="e.g. 90 for quarterly"
              className="mt-1 rounded-none border-[#E2E8F0] font-mono"
              data-testid="recurrence-days-input"
            />
            <p className="text-xs text-[#64748B] mt-1">When completed, auto-creates next record this many days later. Leave blank for one-time.</p>
          </div>
          <div className="border-t border-[#E2E8F0] pt-4">
            <PartsSelector partsUsed={formData.parts_used} setPartsUsed={(pu) => setFormData({ ...formData, parts_used: pu })} allParts={allParts} />
          </div>
          <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="submit-maintenance-button">{editingRecord ? "UPDATE" : "CREATE"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function MaintenancePage({ onLogout, userRole, isFeatureEnabled }) {
  const [maintenance, setMaintenance] = useState([]);
  const [machines, setMachines] = useState([]);
  const [allParts, setAllParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [printOrderData, setPrintOrderData] = useState(null);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      if (sortBy) params.set("sort_by", sortBy);
      if (sortOrder) params.set("sort_order", sortOrder);
      const [maintenanceRes, machinesRes, partsRes] = await Promise.all([
        axios.get(`${API}/maintenance?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/machines`, { headers: getAuthHeaders() }),
        axios.get(`${API}/parts`, { headers: getAuthHeaders() }),
      ]);
      setMaintenance(maintenanceRes.data);
      setMachines(machinesRes.data);
      setAllParts(partsRes.data);
    } catch { toast.error("Failed to load maintenance records"); }
    finally { setLoading(false); }
  }, [search, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(debounce);
  }, [fetchData]);

  const resetForm = () => { setFormData(INITIAL_FORM); setEditingRecord(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const hasParts = formData.parts_used.length > 0 && formData.parts_used.some(p => p.part_id);
      const payload = { ...formData, parts_used: formData.parts_used.filter(p => p.part_id) };

      let recordId;
      if (editingRecord) {
        await axios.put(`${API}/maintenance/${editingRecord.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Maintenance record updated");
        recordId = editingRecord.id;
      } else {
        const resp = await axios.post(`${API}/maintenance`, payload, { headers: getAuthHeaders() });
        toast.success("Maintenance record created");
        recordId = resp.data.id;
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();

      // Auto-print parts order if parts were used
      if (hasParts && recordId) {
        try {
          const orderResp = await axios.get(`${API}/parts-order/${recordId}`, { headers: getAuthHeaders() });
          setPrintOrderData(orderResp.data);
          setTimeout(() => window.print(), 500);
        } catch {
          // Parts order generation failed silently
        }
      }
    } catch { toast.error(editingRecord ? "Update failed" : "Creation failed"); }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      machine_id: record.machine_id,
      title: record.title,
      description: record.description,
      scheduled_date: record.scheduled_date,
      status: record.status,
      parts_used: record.parts_used || [],
      recurrence_days: record.recurrence_days || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this maintenance record?")) return;
    try {
      await axios.delete(`${API}/maintenance/${id}`, { headers: getAuthHeaders() });
      toast.success("Maintenance record deleted");
      fetchData();
    } catch { toast.error("Delete failed"); }
  };

  const handlePrintOrder = async (maintenanceId) => {
    try {
      const resp = await axios.get(`${API}/parts-order/${maintenanceId}`, { headers: getAuthHeaders() });
      setPrintOrderData(resp.data);
      setTimeout(() => window.print(), 300);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate parts order");
    }
  };

  const clearFilters = () => { setSearch(""); setStatusFilter(""); };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const statusOptions = Object.entries(MAINTENANCE_STATUSES).map(([k, v]) => ({ value: k, label: v.label }));

  if (loading) {
    return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><div className="text-[#64748B] font-mono">Loading maintenance records...</div></div></Layout>;
  }

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-4 print:hidden" data-testid="maintenance-page">
        <PageHeader title="Preventative Maintenance" subtitle="Schedule and track equipment maintenance" testId="maintenance-title">
          <div className="flex gap-2">
            <ExportButton endpoint="/export/maintenance" filename="maintenance_export.csv" />
            <Button
              variant="outline"
              onClick={() => { window.open(`${API}/maintenance/calendar.ics`, "_blank"); }}
              className="rounded-none border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white tracking-wide"
              data-testid="subscribe-calendar-button"
            >
              <CalendarDays size={16} className="mr-2" />CALENDAR (.ICS)
            </Button>
            {(userRole === "admin" || userRole === "superintendent") && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const resp = await axios.post(`${API}/maintenance/check-overdue`, {}, { headers: getAuthHeaders() });
                    toast.success(resp.data.message);
                    fetchData();
                  } catch { toast.error("Overdue check failed"); }
                }}
                className="rounded-none border-[#EAB308] text-[#EAB308] hover:bg-[#EAB308] hover:text-white tracking-wide"
                data-testid="check-overdue-maintenance-button"
              >
                <AlertTriangle size={16} className="mr-2" />CHECK OVERDUE
              </Button>
            )}
            <MaintenanceFormDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} editingRecord={editingRecord} formData={formData} setFormData={setFormData} machines={machines} allParts={allParts} onSubmit={handleSubmit} onReset={resetForm} />
          </div>
        </PageHeader>

        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          onClear={clearFilters}
          filters={[
            { key: "status", placeholder: "Statuses", value: statusFilter, onChange: setStatusFilter, options: statusOptions },
          ]}
        />

        {maintenance.length === 0 ? (
          <EmptyState icon={Calendar} message="No maintenance records found. Schedule your first maintenance." />
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs text-[#64748B]" data-testid="maintenance-sort">
              <span>Sort:</span>
              {[{label:"Title",field:"title"},{label:"Machine #",field:"machine_number"},{label:"Date",field:"scheduled_date"},{label:"Status",field:"status"}].map(({label,field}) => (
                <button key={field} onClick={() => handleSortChange(field)} className={`px-2 py-0.5 rounded text-xs transition-colors ${sortBy === field ? "bg-[#0F172A] text-white" : "bg-[#F1F5F9] hover:bg-[#E2E8F0]"}`} data-testid={`sort-btn-${field}`}>
                  {label} {sortBy === field ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                </button>
              ))}
            </div>
            {maintenance.map((record) => <MaintenanceCard key={record.id} record={record} onEdit={handleEdit} onDelete={handleDelete} onPrintOrder={handlePrintOrder} canDelete={userRole === "admin" || userRole === "superintendent"} />)}
          </div>
        )}
      </div>

      <PartsOrderPrintView orderData={printOrderData} />
    </Layout>
  );
}
